import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { RavioliCommonContent } from "../../../../modules/content/components/RavioliCommonContent/RavioliCommonContent";
import { RavioliMedia } from "../../../../modules/content/components/RavioliMedia/RavioliMedia";
import { RavioliRichText } from "../../../../modules/content/components/RavioliRichText/RavioliRichText";
import { RavioliSection } from "../../../../modules/content/components/RavioliSection/RavioliSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <RavioliSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <RavioliCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        {testimonial ? (
          <figure className="grid border border-primary bg-collection text-ink shadow-ravioli-action-lg md:grid-cols-[minmax(18rem,0.8fr)_1.2fr]">
            {testimonial.portrait ? (
              <RavioliMedia
                aspectRatio="1:1"
                className="h-full border-8 border-collection"
                media={testimonial.portrait.image}
                sizes="(min-width: 768px) 35vw, 100vw"
              />
            ) : null}
            <div
              className={cn(
                "flex flex-col justify-center border-t border-primary p-7 md:border-l md:border-t-0 md:p-12 lg:p-16",
                !testimonial.portrait && "md:col-span-full",
              )}
            >
              <blockquote>
                <RavioliRichText
                  className="font-ravioli-display text-4xl font-semibold leading-[1.05] text-ink md:text-5xl"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="mt-8">
                <p className="font-ravioli-body text-sm font-semibold text-primary">
                  {testimonial.name}
                </p>
                {testimonial.attribution ? (
                  <p className="mt-2 font-ravioli-body text-[0.8125rem] text-ink-muted">
                    {testimonial.attribution}
                  </p>
                ) : null}
              </figcaption>
              {testimonial.link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel || undefined}
                  className="mt-6 inline-flex min-h-11 items-center self-start border-b border-primary font-ravioli-body text-sm font-semibold text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                </Link>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>
    </RavioliSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
